import React, { lazy, Suspense, useEffect } from 'react'
import { Route, Routes, BrowserRouter, useLocation, useParams, useNavigate, Navigate } from 'react-router-dom'
import { Result } from 'antd'

import Layout from './layouts/'
import { routeConfig } from './config/routes.config'
import ProtectedRoute from './components/common/ProtectedRoute'
import { isLocalhost } from './helpers/isLocalhost'

const isDevRoute = path => path === '/dev' || path.startsWith('/dev/')

// `/dev` index + `/dev/*` previews are design surfaces. Only register them when
// we're running on localhost — production builds fall through to the 404 catch-all.
export const routes = isLocalhost()
  ? routeConfig
  : routeConfig.filter(r => !isDevRoute(r.path))

// Legacy routes array (kept for reference, can be removed later)
const routesLegacy = [

  {
    path: '/chatbot',
    Component: lazy(() => import('./components/chatbot/components/Chatbot')),
    exact: true,
    },

  {
    path: '/design',
    Component: lazy(() => import('./components/design/components/Design')),
    exact: true,
    },
  {
    path: '/secondary-sidebar',
    Component: lazy(() => import('./components/common/SecondarySidebar/SecondarySidebarExample')),
    exact: true,
    },

    {
    path: '/',
    Component: lazy(() => import('./components/dashboard/Dashboard')),
    exact: true,
    },
    // intro-business-information removed — superseded by Business profile menu.
    {
      path: '/dashboard',
      Component: lazy(() => import('./components/dashboard/Dashboard')),
      exact: true,
      },
      {
        path: '/dashboard-analytics',
        Component: lazy(() => import('./components/dashboard/DashboardAnalytics')),
        exact: true,
      },
      {
        path: '/profile',
        Component: lazy(() => import('./components/myProfile/components/MyProfileDetails')),
        exact: true,
      }, 
      {
        path: '/todos',
        Component: lazy(() => import('./components/todos/components/Todos')),
        exact: true,
      },        
      {
        path: '/my-profile-edit',
        Component: lazy(() => import('./components/myProfile/components/MyProfileForm')),
        exact: true,
      },  
    
    
    {
        path: '/customers/list',
        Component: lazy(() => import('./components/customers/components/Customers')),
        exact: true,
    },
    {
        path: '/customers/customer-details/:id',
        Component: lazy(() => import('./components/customers/components/CustomerDetails')),
        exact: false,
    }, 
    {
        path: '/customers/customer-edit/:id',
        Component: lazy(() => import('./components/customers/components/CustomerForm')),
        exact: false,
    },  
    // {
    //     path: '/customers/new',
    //     Component: lazy(() => import('./components/customers/components/CustomerDetails')),
    //     exact: false,
    // },  
    {
        path: '/customers-statements',
        Component: lazy(() => import('./components/customers-statements/components/CustomersStatementsDetails')),
        exact: false,
    },  


    {
        path: '/invoices/list',
        Component: lazy(() => import('./components/invoices/components/Invoices')),
        exact: true,
    },


    
    {
      path: '/invoices/invoice-details/:id',
      Component: lazy(() => import('./components/invoices/components/InvoiceDetails')),
      exact: false,
    }, 
    
    {
      path: '/invoices/invoice-edit/:id',
      Component: lazy(() => import('./components/invoices/components/InvoiceForm')),
      exact: false,
    },  
    {
      path: '/invoices/new',
      Component: lazy(() => import('./components/invoices/components/InvoiceForm')),
      exact: true,
    },       
    {
        path: '/estimates/list',
        Component: lazy(() => import('./components/estimates/components/Estimates')),
        exact: true,
    },
    {
      path: '/estimates/estimate-details/:id',
      Component: lazy(() => import('./components/estimates/components/EstimateDetail')),
      exact: false,
    },
    {
      path: '/estimates/estimate-edit/:id',
      Component: lazy(() => import('./components/estimates/components/EstimateForm')),
      exact: false,
    },     
    {
      path: '/estimates/new',
      Component: lazy(() => import('./components/estimates/components/EstimateForm')),
      exact: true,
    }, 

    {
      path: '/sales-tax',
      Component: lazy(() => import('./components/sales-tax/components/SalesTaxes')),
      exact: true,
    },   

    {
      path: '/sales-tax/sales-tax-details/:id',
      Component: lazy(() => import('./components/sales-tax/components/SalesTaxDetails')),
      exact: false,
    }, 
    {
      path: '/sales-tax/new',
      Component: lazy(() => import('./components/sales-tax/components/SalesTaxDetails')),
      exact: true,
    },  

    {
      path: '/receive-payments',
      Component: lazy(() => import('./components/receive-payments/components/ReceivePayments')),
      exact: true,
    },
    {
      path: '/receive-payments/add-payment',
      Component: lazy(() => import('./components/receive-payments/components/ReceivePaymentForm')),
      exact: true,
    },

    {
      path: '/settings',
      Component: lazy(() => import('./components/Settings/components/Settings')),
      exact: true,
    }, 
    {
      path: '/admin-settings/configurations',
      Component: lazy(() => import('./components/admin-settings/configurations/components/IncConfigurations')),
      exact: true,
    }, 

    // /business-profile is now handled by routes.config.js
    // as a 3-page group (Branding / Business info / Welcome page). 
    {
      path: '/admin-settings/business-billing',
      Component: lazy(() => import('./components/admin-settings/company/components/CompanyBillingDetails')),
      exact: true,
  }, 
  //   {
  //     path: '/admin-settings/smtp/list',
  //     Component: lazy(() => import('./components/admin-settings/SMTP/components/SMTPList')),
  //     exact: true,
  // }, 
  //   {
  //     path: '/admin-settings/smtp-details/:id',
  //     Component: lazy(() => import('./components/admin-settings/SMTP/components/SMTPDashboard')),
  //     exact: true,
  // }, 
    {
      path: '/licenses',
      Component: lazy(() => import('./components/licenses/components/Licenses')),
      exact: true,
  }, 
  //   {
  //     path: '/admin-settings/license-details/:id',
  //     Component: lazy(() => import('./components/licenses/components/_LicenseDetails')),
  //     exact: true,
  // },
  {
    path: '/licenses/success',
    Component: lazy(() => import('./components/licenses/components/SuccessPage')),
    exact: true,
  },
   
  
  {
    path: '/bank-accounts',
    Component: lazy(() => import('./components/admin-settings/bank-accounts/components/PaymentStripe')),
    exact: true,
  }, 

    {
        path: '/items/list',
        Component: lazy(() => import('./components/services/components/Services')),
        exact: true,
    },  
    {
        path: '/items/item-details/:id',
        Component: lazy(() => import('./components/services/components/ServiceDetails')),
        exact: false,
    },  
    {
        path: '/services/service-edit/:id',
        Component: lazy(() => import('./components/services/components/ServiceForm')),
        exact: false,
    },  
    {
        path: '/items/new',
        Component: lazy(() => import('./components/services/components/ServiceNew')),
        exact: true,
    },   
    {
        path: '/inventory-items/list',
        Component: lazy(() => import('./components/inventory-items/components/InventoryItems')),
        exact: true,
    },
    {
        path: '/inventory-items/item-details/:id',
        Component: lazy(() => import('./components/inventory-items/components/InventoryItemDetails')),
        exact: false,
    },
    {
        path: '/inventory-items/new',
        Component: lazy(() => import('./components/inventory-items/components/InventoryItemForm')),
        exact: true,
    },
    {
        path: '/inventory/categories',
        Component: lazy(() => import('./components/inventory-categories/components/InventoryCategoriesList')),
        exact: true,
    },
    
    
    // {
    //     path: '/inventory-items/item-details/:id',
    //     Component: lazy(() => import('./components/inventory-items/components/InventoryItemForm')),
    //     exact: false,
    // },
    // {
    //     path: '/inventory-items/new',
    //     Component: lazy(() => import('./components/inventory-items/components/InventoryItemForm')),
    //     exact: true,
    // },  
   
    {
        path: '/items/items-category/list',
        Component: lazy(() => import('./components/services-category/components/ServicesCategory')),
        exact: true,
    },  
    // {
    //     path: '/services/services-category/services-category-details/:id',
    //     Component: lazy(() => import('./components/services-category/components/ServicesCategoryDetails')),
    //     exact: false,
    // },  
    // {
    //     path: '/services/services-category/new',
    //     Component: lazy(() => import('./components/services-category/components/ServicesCategoryDetails')),
    //     exact: true,
    // },     

/*
|
|
| Configurable Templates
|
|
*/
    {
        path: '/configurable-templates',
        Component: lazy(() => import('./components/service-configurable-templates')),
        exact: true,
    },
    {
        path: '/configurable-groups',
        Component: lazy(() => import('./components/configurable-groups/components/ConfigurableGroupsList')),
        exact: true,
    },
    {
        path: '/configurable-groups/new',
        Component: lazy(() => import('./components/configurable-groups/components/ConfigurableGroupDetails')),
        exact: true,
    },
    {
        path: '/configurable-groups/:id',
        Component: lazy(() => import('./components/configurable-groups/components/ConfigurableGroupDetails')),
        exact: false,
    },

/*
|
|
| Purchases
|
|
*/

{
  path: '/suppliers/list',
  Component: lazy(() => import('./components/vendors/components/Suppliers')),
  exact: true,
},  
{
  path: '/suppliers/supplier-details/:id',
  Component: lazy(() => import('./components/vendors/components/SupplierDetails')),
  exact: false,
},  
// {
//   path: '/suppliers/new',
//   Component: lazy(() => import('./components/vendors/components/SupplierDetails')),
//   exact: true,
// }, 

/*
|
|
| Bills
|
|
*/




{
  path: '/bills/list',
  Component: lazy(() => import('./components/bills/components/Bills')),
  exact: true,
},   

{
path: '/bills/bill-details/:id',
Component: lazy(() => import('./components/bills/components/BillDetails')),
exact: false,
}, 

{
path: '/bills/bill-edit/:id',
Component: lazy(() => import('./components/bills/components/BillForm')),
exact: false,
},  
{
path: '/bills/new',
Component: lazy(() => import('./components/bills/components/BillForm')),
exact: true,
},



  {
    path: '/users/list',
    Component: lazy(() => import('./components/users/components/Users')),
    exact: true,
},  
//   {
//     path: '/users/new',
//     Component: lazy(() => import('./components/users/components/UserDetails')),
//     exact: true,
// },    
    
  {
    path: '/users/user-edit/:id',
    Component: lazy(() => import('./components/users/components/UserDetails')),
    exact: false,
},   
  {
    path: '/users/user-details/:id',
    Component: lazy(() => import('./components/users/components/UserDetails')),
    exact: false,
},   
    

/*
|
|
| Reports
|
|
*/


   

{
  path: '/reports',
  Component: lazy(() => import('./components/reports/components/ReportsLanding')),
  exact: true,
},

/*
|
|
| Inventory Reports
|
|
*/

{
  path: '/reports/inventory',
  Component: lazy(() => import('./components/reports/components/inventory/InventoryReport')),
  exact: true,
},

{
  path: '/reports/inventory/transactions',
  Component: lazy(() => import('./components/reports/components/inventory/InventoryTransactionsReport')),
  exact: true,
},  
{
  path: '/reports/payment-collection',
  Component: lazy(() => import('./components/reports/components/payments/PaymentReport')),
  exact: true,
},  

{
  path: '/reports/invoices',
  Component: lazy(() => import('./components/reports/components/sales/SalesReport')),
  exact: true,
},  


{
  path: '/reports/bills',
  Component: lazy(() => import('./components/reports/components/bills/BillReport')),
  exact: true,
},

{
  path: '/reports/taxes',
  Component: lazy(() => import('./components/reports/components/taxes/TaxReport')),
  exact: true,
},

{
  path: '/reports/emails',
  Component: lazy(() => import('./components/reports/components/emails/EmailReport')),
  exact: true,
},


/*
|
|
| Appointment
|
|
*/

{
  path: '/calendar',
  Component: lazy(() => import('./components/calendars/components/Calendars')),
  exact: true,
},  



  // END VAREXE

  // Dashboards
//   {
//     path: '/dashboard/alpha',
//     Component: lazy(() => import('pages/dashboard/alpha')),
//     exact: true,
//   }, 

/*
| Auth Card version
*/

// {
//   path: '/auth/logout',
//   Component: lazy(() => import('./components/auth/basic/Logout')),
//   exact: true,
// },
// {
//     path: '/auth/login',
//     Component: lazy(() => import('./components/auth/card/Login')),
//     exact: true,
//   },
//   {
//     path: '/auth/forget-password',
//     Component: lazy(() => import('./components/auth/card/ForgetPassword')),
//     exact: true,
//   },  
//   {
//     path: '/auth/register',
//     Component: lazy(() => import('./components/auth/card/Registration')),
//     exact: true,
//   },  
/*
| End Auth Card version
*/
/*
| Auth Split version
*/
{
  path: '/auth/logout',
  Component: lazy(() => import('./components/auth/split/Logout')),
  exact: true,
},
{
    path: '/auth/login',
    Component: lazy(() => import('./components/auth/split/Login')),
    exact: true,
  },
  {
    path: '/auth/forget-password',
    Component: lazy(() => import('./components/auth/split/PasswordForget')),
    exact: true,
  },  
  {
    path: '/auth/reset-password',
    Component: lazy(() => import('./components/auth/split/PasswordReset')),
    exact: true,
  },    
  {
    path: '/auth/register',
    Component: lazy(() => import('./components/auth/split/Registration')),
    exact: true,
  },
  {
    path: '/auth/register/park/pending-creating-database',
    Component: lazy(() => import('./components/auth/split/WaitingForDatabase')),
    exact: true,
  },

  {
    path: '/errors/404',
    Component: lazy(() => import('./components/errors/Error404')),
    exact: true,
  },

/*
| END Split version
*/


{
  path: '/support',
  Component: lazy(() => import('./components/support/Support')),
  exact: true,
},




/*
|
|
| PUBLIC
|
|
*/

{
  path: '/park/pending-creating-database',
  Component: lazy(() => import('./components/public/park/CreatingDatabase')),
  exact: true,
},

/*
|
|
| SharePoint
|
|
*/

{
  path: 'public/sharepoint/:orgPublicId/sale/:invoicePublicId',
  Component: lazy(() => import('./components/public/sharePoint/SharePoint')),
  exact: true,
},
{
  path: 'public/sharepoint/:orgPublicId/estimate/:estimatePublicId',
  Component: lazy(() => import('./components/public/sharePoint/SharePoint')),
  exact: true,
},

/*
| privacy
*/


/*
| Appatch
*/

{
  path: '/appatch',
  Component: lazy(() => import('./01_site_appatch/components/landing/components/Landing')),
  exact: true,
},
{
  path: '/appatch/services/json-formatter',
  Component: lazy(() => import('./01_site_appatch/components/jsonFormatter/components/JsonFormatter')),
  exact: true,
},

]

const Router = ({ history, routerAnimation, onAppMounted }) => {
  useEffect(() => {
    onAppMounted?.();
  }, [onAppMounted]);
  return (
    <BrowserRouter history={history}>
      <RouterWithPath routerAnimation={routerAnimation} />
    </BrowserRouter>
  );
};

const RouterWithPath = ({ routerAnimation }) => {
  const location = useLocation(); // ✅ Get current pathname

  return (
    <Layout location={location}> {/* ✅ Pass pathname to Layout */}
      <Routes>
        {routes.map(({ path, Component, exact }) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute path={path}>
                <Wrapper Component={Component} routerAnimation={routerAnimation} />
              </ProtectedRoute>
            }
          />
        ))}
        {/* Catch-all route for 404 - must be last */}
        <Route
          path="*"
          element={
            <Result
              status="404"
              title="404"
              subTitle="Sorry, the page you visited does not exist."
            />
          }
        />
      </Routes>
    </Layout>
  );
};

// Route components receive `params` plus `location` / `navigate` for query-string UX (e.g. detail tabs).
const Wrapper = ({ Component, routerAnimation }) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className={routerAnimation}>
      <Suspense fallback={null}>
        <Component params={params} location={location} navigate={navigate} />
      </Suspense>
    </div>
  );
};

export default Router
